import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';

import { db, storage, auth } from '../../../firebase.init';
import { useUserRole } from '../../../hooks/useUserRole';
import { createTicket } from '../../Tickets/Tickets';

import {
    collection,
    getDocs,
    query,
    where,
    updateDoc,
    doc,
    addDoc,
    collectionGroup,
    arrayUnion,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useAuthState } from 'react-firebase-hooks/auth';
import {
    Modal as BootstrapModal,
    Button,
    Form,
    Col,
    Row,
    Tabs,
    Tab,
    Card,
    ProgressBar,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_red.css';
import * as XLSX from 'xlsx';
import { format, parse } from 'date-fns';


const ALL_TABS = ['section1', 'section2', 'section3', 'section4'];

const TAB_TITLES = {
    section1: 'Building Info',
    section2: 'Process Decisions',
    section3: 'Reschedule Reports',
    section4: 'Create Ticket',
};

const TICKET_PRIORITY_OPTIONS = ['Low', 'Medium', 'High'];

const TASK_TYPE_OPTIONS = [
    { value: 'inspection', label: 'Inspection' },
    { value: 'estimate_request', label: 'Estimate Request' },
    { value: 'review_inspection', label: 'Review Inspection' },
    { value: 'quality_control', label: 'Quality Control' },
    { value: 'check_remedials', label: 'Check Remedials' },
    { value: 'remedial_booking', label: 'Remedial Booking' },
    { value: 'carpentry_work', label: 'Carpentry Work' },
    { value: 'review_carpentry', label: 'Review Carpentry' },
];

const buildingTypeOptions = [
    { value: 'Assembly and Recreation', label: 'Assembly and Recreation' },
    { value: 'Health Care', label: 'Health Care' },
    { value: 'Industrial', label: 'Industrial' },
    { value: 'Mixed Use', label: 'Mixed Use' },
    { value: 'Office', label: 'Office' },
    { value: 'Residential', label: 'Residential' },
    { value: 'Shop/Commercial', label: 'Shop/Commercial' },
    { value: 'Storage', label: 'Storage' },
    { value: 'Other', label: 'Other' },
];

const selectStyles = {
    control: (provided, { isFocused }) => ({
        ...provided,
        borderRadius: '0.375rem',
        padding: '0 0.5rem',
        borderColor: isFocused ? '#dc3545' : '#ced4da',
        boxShadow: isFocused ? '0 0 0 0.2rem rgba(220,53,69,.25)' : 'none',
        minHeight: '38px',
        height: '38px',
        outline: 'none',
        '&:hover': { borderColor: '#dc3545' },
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        cursor: 'pointer',
    }),
    menu: (provided) => ({ ...provided, width: '260px' }),
    option: (provided, { isFocused, isSelected }) => ({
        ...provided,
        backgroundColor: isSelected ? '#dc3545' : isFocused ? '#ffe5e5' : 'white',
        color: isSelected ? 'white' : '#333',
        cursor: 'pointer',
        minHeight: '38px',
        display: 'flex',
        alignItems: 'center',
        '&:active': { backgroundColor: '#dc3545' },
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#282828ff',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#282828ff',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        color: '#a71d2a',
        '&:hover': { color: '#dc3545' },
    }),
    clearIndicator: (provided) => ({
        ...provided,
        color: '#a71d2a',
        '&:hover': { color: '#dc3545' },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

const REQUIRED_COLUMNS = [
    'Property Name',
    'Building Address',
    'Building City',
    'Postcode',
    'Floors',
    'Building Type',
    'Client Name',
    'Client Email',
    'Accountable Person',
    'Communal Doors',
    'Residential Doors',
    'Re-inspection',
    'Visit Twice',
];

const AVATAR_COLORS = [
    { bg: '#E6F1FB', text: '#0C447C' },
    { bg: '#E1F5EE', text: '#085041' },
    { bg: '#EEEDFE', text: '#3C3489' },
    { bg: '#FAECE7', text: '#712B13' },
    { bg: '#FAEEDA', text: '#633806' },
    { bg: '#FBEAF0', text: '#72243E' },
];

function getInitials(name) {
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
}

function CustomCheck({ checked, size = 16 }) {
    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: '50%',
                border: checked ? `1.5px solid #378ADD` : `1.5px solid #ccc`,
                background: checked ? '#378ADD' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.15s',
                flexShrink: 0,
            }}
        >
            {checked && (
                <svg width={size * 0.55} height={size * 0.4} viewBox="0 0 9 6" fill="none">
                    <path d="M1 3L3.5 5.5L8 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
            )}
        </div>
    );
}

const InspectionModal = ({
    directOpen,
    onDirectClose,
    inspectores = [],
    visibleTabs = ALL_TABS,
    initialTab,
    onSaved,
    onScheduled,
    assignableUsers = [],
}) => {
    const [user] = useAuthState(auth);
    const userData = useUserRole();

    const tabs = ALL_TABS.filter((t) => visibleTabs.includes(t));
    const firstTab = initialTab && tabs.includes(initialTab) ? initialTab : tabs[0];
    const isTicketFlow = tabs.includes('section4');

    const isMobile = window.innerWidth < 768;
    const formRef = useRef(null);
    const [activeTab, setActiveTab] = useState(firstTab);
    const [submitting, setSubmitting] = useState(false);

    const today = new Date();
    const [plannedDate, setPlannedDate] = useState(today);
    const [commDate, setCommDate] = useState(null);
    const [resiDate, setResiDate] = useState(null);

    const [buildingName, setBuildingName] = useState('');

    const [selectedBuildingInspector, setSelectedBuildingInspector] = useState(null);
    const [selectedBuildingType, setSelectedBuildingType] = useState(null);

    const [distance, setDistance] = useState(null);
    const [duration, setDuration] = useState(null);
    const [loadingEstimate, setLoadingEstimate] = useState(null);

    const [totalTime, setTotalTime] = useState(null);
    const avgSurveyTime = 350;

    const handleCalculateTime = () => {
        const formData = new FormData(formRef.current);
        const communalDoors = parseInt(formData.get('totalCommunalDoors'), 10) || 0;
        const resiDoors = parseInt(formData.get('totalResiDoors'), 10) || 0;

        const totalDoors = communalDoors + resiDoors;
        const totalSeconds = totalDoors * avgSurveyTime;

        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = totalSeconds % 60;

        setTotalTime({ hrs, mins, secs });
    };

    const handleCalculateDistance = async () => {
        const formData = new FormData(formRef.current);

        const baseAddress = 'Foundation House';
        const baseCity = 'Reigate';

        const destAddress = formData.get('buildingAddress');
        const destCity = formData.get('buildingCity');

        if (!baseAddress || !baseCity) {
            setDistance(null);
            setDuration(null);
            return;
        }

        setLoadingEstimate(true);

        try {
            const response = await fetch('/api/getDistance', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ baseAddress, baseCity, destAddress, destCity }),
            });

            const data = await response.json();

            setDistance(data.distanceMiles ?? null);
            setDuration(data.durationMinutes ?? null);
        } catch (err) {
            console.error('Distance error:', err);
            setDistance(null);
            setDuration(null);
            setTotalTime(null);
        } finally {
            setLoadingEstimate(false);
        }
    };

    const [buildings, setBuildings] = useState([]);
    const [from, setFrom] = useState(null);
    const [to, setTo] = useState(null);
    const [unavailableRanges, setUnavailableRanges] = useState([]);
    const [unavailableSaveRanges, setUnavailableSaveRanges] = useState([]);
    const toRef = useRef(null);

    const [availableFrom, setAvailableFrom] = useState(null);
    const [availableTo, setAvailableTo] = useState(null);

    const [baseData, setBaseData] = useState(null);
    const [includeExpiring, setIncludeExpiring] = useState(false);
    const [overdue, setOverdue] = useState(null);
    const [loadingReschedule, setLoadingReschedule] = useState(false);

    const [tbTicketTitle, setTbTicketTitle] = useState('');
    const [tbTicketTitleTouched, setTbTicketTitleTouched] = useState(false);
    const [tbTicketDescription, setTbTicketDescription] = useState('');
    const [tbAssignee, setTbAssignee] = useState('');
    const [tbPriority, setTbPriority] = useState('Medium');
    const [tbTaskType, setTbTaskType] = useState('inspection');
    const [tbDueDate, setTbDueDate] = useState('');
    const [tbTicketFile, setTbTicketFile] = useState(null);
    const [submittingTicketBuilding, setSubmittingTicketBuilding] = useState(false);

    const resetTicketFields = () => {
        setTbTicketTitle(''); setTbTicketTitleTouched(false); setTbTicketDescription('');
        setTbAssignee(''); setTbPriority('Medium'); setTbTaskType('inspection');
        setTbDueDate(''); setTbTicketFile(null);
    };

    useEffect(() => {
        if (directOpen && isTicketFlow && !tbAssignee && assignableUsers.length > 0) {
            const self = assignableUsers.find((u) => u.uid === user?.uid);
            if (self) setTbAssignee(self.uid);
        }
    }, [directOpen, assignableUsers, user?.uid]);

    const handleCreateBuildingAndTicket = async () => {
        if (!formRef.current) return;

        const formData = new FormData(formRef.current);

        const fieldLabels = {
            propertySurveyed: 'Property Surveyed',
            reportId: 'Report ID',
            buildingType: 'Building Type',
            buildingAddress: 'Building Address',
            buildingCity: 'Building City',
            buildingPostcode: 'Building Postcode',
            floors: 'Number of Floors',
            clientName: 'Client Name',
            email: 'Client Email',
            accountablePerson: 'Accountable Person',
            date: 'Planned visit date',
        };

        const requiredBuildingFields = Object.keys(fieldLabels);

        const missingBuildingFields = requiredBuildingFields.filter((field) => {
            const value = formData.get(field);
            return !value || value.trim() === '';
        });

        if (missingBuildingFields.length > 0) {
            const missingLabels = missingBuildingFields.map((field) => fieldLabels[field]);
            alert(`Please fill all required building fields on "${TAB_TITLES.section1}":\n\n${missingLabels.join('\n')}`);
            setActiveTab('section1');
            return;
        }

        if (!tbTicketTitle.trim()) { alert('Ticket Title is required.'); setActiveTab('section4'); return; }
        if (!tbAssignee) { alert('Please select who this ticket is assigned to.'); setActiveTab('section4'); return; }
        if (!tbDueDate) { alert('Please select a ticket due date & time.'); setActiveTab('section4'); return; }

        const assignee = assignableUsers.find((u) => u.uid === tbAssignee);
        if (!assignee) { alert('Select a valid assignee.'); return; }

        setSubmittingTicketBuilding(true);

        try {
            const now = new Date();
            const formattedDate = format(now, 'yyyy-MM-dd HH:mm:ss');

            const buildingPhotoInput = formRef.current.querySelector('#buildingPhoto');
            const logoInput = formRef.current.querySelector('#buildingLogo');
            const contractorCertificateInput = formRef.current.querySelector('#contractorCertificate');

            const buildingPhoto = buildingPhotoInput?.files?.[0] || null;
            const logo = logoInput?.files?.[0] || null;
            const contractorCertificate = contractorCertificateInput?.files?.[0] || null;

            const totalCommunal = parseInt(formData.get('totalCommunalDoors')) || 0;
            const totalResi = parseInt(formData.get('totalResiDoors')) || 0;

            const dateValue = formData.get('date') || '';
            const dateTime = dateValue ? `${dateValue} 10:00:00` : '';

            const propertyName = (formData.get('propertySurveyed') || '').trim();

            const buildingDataToSave = {
                reportId: formData.get('reportId') || '',
                clientName: formData.get('clientName') || '',
                clientAddress: formData.get('buildingAddress') || '',
                client_address: '',
                client_city: '',
                client_postcode: '',
                city: formData.get('buildingCity') || '',
                postcode: formData.get('buildingPostcode') || '',
                buildingName: propertyName,
                floors: formData.get('floors') || '',
                buildingType: formData.get('buildingType') || '',
                personInCharge: formData.get('accountablePerson') || '',
                nextDate: formData.get('nextCommunalSurveyDate') || '',
                nextDateResidential: formData.get('nextResidentialSurveyDate') || '',
                dateCreated: formattedDate,
                date: dateTime,
                uid: '',
                surveryCompleted: false,
                buildingLink: '',
                logo: '',
                email: formData.get('email') || '',
                addedBy: user?.uid || '',
                totalCommunalDoors: totalCommunal,
                totalResiDoors: totalResi,
                contractorCertificate: '',
                completedBy: selectedBuildingInspector?.label || '',
                completedByUid: selectedBuildingInspector?.value || '',
            };

            const buildingRef = await addDoc(collection(db, 'Reports'), buildingDataToSave);
            await updateDoc(doc(db, 'Reports', buildingRef.id), { uid: buildingRef.id });

            if (buildingPhoto) await uploadFile(buildingPhoto, buildingRef.id, 'buildingLink');
            if (logo) await uploadFile(logo, buildingRef.id, 'logo');
            if (contractorCertificate) await uploadFile(contractorCertificate, buildingRef.id, 'contractorCertificate');

            const ticketRef = await createTicket({
                title: tbTicketTitle.trim(),
                description: tbTicketDescription,
                assigneeId: assignee.uid,
                assigneeRole: assignee.user_role,
                assigneeLabel: assignee.name || assignee.email,
                actorUid: user?.uid,
                priority: tbPriority,
                taskType: tbTaskType,
                dueDate: new Date(tbDueDate),
                buildingId: buildingRef.id,
                buildingName: propertyName,
            });

            if (tbTicketFile) {
                const sr = ref(storage, `tickets/${ticketRef.id}/${tbTicketFile.name}`);
                await uploadBytes(sr, tbTicketFile);
                const url = await getDownloadURL(sr);
                await updateDoc(ticketRef, { image: url });
            }

            await updateDoc(doc(db, 'Reports', buildingRef.id), {
                ticket_id: ticketRef.id,
                linked_ticket_ids: arrayUnion(ticketRef.id),
            });

            toast.success(`Building "${propertyName}" and its ticket were created!`, {
                toastId: `tb_success_${ticketRef.id}`,
                icon: <FontAwesomeIcon icon={faCircleCheck} size="lg" style={{ color: 'green' }} />,
                autoClose: 3000,
                style: {
                    backgroundColor: '#e6ffed',
                    color: '#064e3b',
                    borderLeft: '4px solid green',
                },
            });

            resetTicketFields();
            formRef.current?.reset();
            setSelectedBuildingInspector(null);
            setSelectedBuildingType(null);
            setPlannedDate(today);
            setCommDate(null);
            setResiDate(null);
            setBuildingName('');

            onDirectClose?.();
            onSaved?.();
        } catch (err) {
            console.error('Error creating building + ticket:', err);
            toast.error(err.message || 'Failed to create building and ticket.', {
                toastId: 'tb_error',
                style: {
                    backgroundColor: '#fee',
                    color: '#c00',
                    borderLeft: '4px solid red',
                },
                icon: <FontAwesomeIcon icon={faCircleXmark} size="lg" style={{ color: 'red' }} />,
            });
        } finally {
            setSubmittingTicketBuilding(false);
        }
    };

    const handleSelect = async (key) => {
        setActiveTab(key);

        if (key === 'section3' && !overdue && !loadingReschedule) {
            try {
                setLoadingReschedule(true);

                const result = await reSchedule();

                const zeroResiDoors = (arr) =>
                    arr.map((r) => ({
                        ...r,
                        totalResiDoors: 0,
                        visitTwice: 'No',
                        buildingType: 'Communal',
                        selected: false,
                    }));

                const zeroCommunalDoors = (arr) =>
                    arr.map((r) => ({
                        ...r,
                        totalCommunalDoors: 0,
                        buildingType: 'Residential',
                        selected: false,
                    }));

                const normalizedResult = {
                    overdue: {
                        communal5OrAbove: zeroResiDoors(result.overdue.communal5OrAbove),
                        communalBelow5Floors: zeroResiDoors(result.overdue.communalBelow5Floors),
                        residentialBuildings: zeroCommunalDoors(result.overdue.residentialBuildings),
                    },
                    expiringSoon: {
                        communal5OrAbove: zeroResiDoors(result.expiringSoon.communal5OrAbove),
                        communalBelow5Floors: zeroResiDoors(result.expiringSoon.communalBelow5Floors),
                        residentialBuildings: zeroCommunalDoors(result.expiringSoon.residentialBuildings),
                    },
                };

                const parseDate = (str) => {
                    const [day, month, year] = str.split('/').map(Number);
                    return new Date(year, month - 1, day);
                };

                const formatDate = (date) =>
                    `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

                const addNextDate = (arr, monthsToAdd) =>
                    arr.map((r) => {
                        const lastInspection = parseDate(r.date);
                        const nextDate = new Date(lastInspection);
                        nextDate.setMonth(nextDate.getMonth() + monthsToAdd);

                        return {
                            ...r,
                            nextDate: formatDate(nextDate),
                        };
                    });

                const withNextDates = {
                    overdue: {
                        communal5OrAbove: addNextDate(normalizedResult.overdue.communal5OrAbove, 3),
                        communalBelow5Floors: addNextDate(normalizedResult.overdue.communalBelow5Floors, 12),
                        residentialBuildings: addNextDate(normalizedResult.overdue.residentialBuildings, 12),
                    },
                    expiringSoon: {
                        communal5OrAbove: addNextDate(normalizedResult.expiringSoon.communal5OrAbove, 3),
                        communalBelow5Floors: addNextDate(normalizedResult.expiringSoon.communalBelow5Floors, 12),
                        residentialBuildings: addNextDate(normalizedResult.expiringSoon.residentialBuildings, 12),
                    },
                };

                setBaseData(withNextDates);
                setOverdue(withNextDates.overdue);
            } catch (err) {
                console.error(err);
            } finally {
                setLoadingReschedule(false);
            }
        }
    };

    const toggleIncludeExpiring = (checked) => {
        setIncludeExpiring(checked);

        if (!baseData) return;

        const dedupe = (arr) => {
            const seen = new Set();
            return arr.filter((r) => {
                const id = r.uid || r.buildingName;
                if (seen.has(id)) return false;
                seen.add(id);
                return true;
            });
        };

        if (checked) {
            setOverdue((prev) => ({
                communal5OrAbove: dedupe([...baseData.overdue.communal5OrAbove, ...baseData.expiringSoon.communal5OrAbove]),
                communalBelow5Floors: dedupe([...baseData.overdue.communalBelow5Floors, ...baseData.expiringSoon.communalBelow5Floors]),
                residentialBuildings: dedupe([...baseData.overdue.residentialBuildings, ...baseData.expiringSoon.residentialBuildings]),
            }));
        } else {
            setOverdue(baseData.overdue);
        }
    };

    async function reSchedule() {
        const snap = await getDocs(collectionGroup(db, 'Reports'));

        const reportsByBuilding = new Map();

        snap.forEach((docSnap) => {
            const d = docSnap.data();
            if (!d?.buildingName || !d?.date) return;

            const uid = d.uid;
            const entry = { ...d, uid };

            if (!reportsByBuilding.has(d.buildingName)) {
                reportsByBuilding.set(d.buildingName, [entry]);
            } else {
                reportsByBuilding.get(d.buildingName).push(entry);
            }
        });

        reportsByBuilding.forEach((reports) => {
            reports.sort((a, b) => new Date(b.date) - new Date(a.date));
        });

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const in30Days = new Date(today);
        in30Days.setDate(in30Days.getDate() + 30);
        in30Days.setHours(23, 59, 59, 999);

        const getFloors = (v) => Number(String(v || '').replace(/\D/g, '')) || 0;

        const getNextDueDate = (lastDate, monthsToAdd) => {
            const next = new Date(lastDate);
            next.setMonth(next.getMonth() + monthsToAdd);
            return next;
        };

        const formatDate = (date) =>
            `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;

        const buildingNames = Array.from(reportsByBuilding.keys());

        const detectedBuildings = await Promise.all(
            buildingNames.map(async (buildingName) => {
                const allReportsForBuilding = reportsByBuilding.get(buildingName);
                const latest = allReportsForBuilding[0];

                let isCommunal = false;
                let isResidential = false;
                let totalCommunalDoors = 0;
                let totalResiDoors = 0;
                let communalLastDate = null;
                let residentialLastDate = null;

                for (const report of allReportsForBuilding) {
                    if (!report.uid) continue;

                    const communalSnap = await getDocs(collection(db, 'Reports', report.uid, 'DoorReports'));
                    const communalDocs = communalSnap.docs.filter((doc) => doc.data()?.addedBy);

                    const residentialSnap = await getDocs(collection(db, 'Reports', report.uid, 'ResidentialReports'));
                    const residentialDocs = residentialSnap.docs.filter((doc) => doc.data()?.addedBy);

                    if (communalDocs.length > 0 && !communalLastDate) {
                        isCommunal = true;
                        totalCommunalDoors = Math.max(totalCommunalDoors, communalDocs.length);

                        const communalDates = communalDocs
                            .map((doc) => doc.data()?.date)
                            .filter(Boolean)
                            .map((date) => new Date(date))
                            .filter((date) => !Number.isNaN(date.getTime()))
                            .sort((a, b) => a - b);

                        communalLastDate = communalDates[0] || null;
                    }

                    if (residentialDocs.length > 0 && !residentialLastDate) {
                        isResidential = true;
                        totalResiDoors = Math.max(totalResiDoors, residentialDocs.length);

                        const residentialDates = residentialDocs
                            .map((doc) => doc.data()?.date)
                            .filter(Boolean)
                            .map((date) => new Date(date))
                            .filter((date) => !Number.isNaN(date.getTime()))
                            .sort((a, b) => a - b);

                        residentialLastDate = residentialDates[0] || null;
                    }
                }

                const typeStr =
                    [isCommunal ? 'communal' : null, isResidential ? 'residential' : null]
                        .filter(Boolean)
                        .join(', ') || 'unknown';

                return {
                    buildingName,
                    uid: latest.uid || '',
                    clientAddress: latest.clientAddress || '',
                    buildingAddress: `${latest.buildingName || ''}, ${latest.clientAddress || ''}`,
                    buildingCity: latest.city || '',
                    totalCommunalDoors: totalCommunalDoors || latest.totalCommunalDoors || 0,
                    totalResiDoors: totalResiDoors || latest.totalResiDoors || 0,
                    visitTwice: latest.visitTwice || 'No',
                    reinspection: latest.reinspection || 'No',
                    buildingEmail: latest.email || '',
                    postcode: latest.postcode || '',
                    floors: getFloors(latest.floors),
                    buildingType: typeStr,
                    clientName: latest.clientName || '',
                    accountablePerson: latest.personInCharge || '',
                    autoSchedule: latest.autoSchedule !== false,
                    communalLastDate,
                    residentialLastDate,
                };
            })
        );

        const skipClients = ['Rent Kooky', 'Property Fusion'];
        const skipEmails = ['test@gmail.com'];

        const isSkipped = (b) => skipClients.includes(b.clientName) || skipEmails.includes(b.buildingEmail);

        const communal5OrAbove = [];
        const communalBelow5Floors = [];
        const residentialBuildings = [];
        const expiringSoonCommunal5OrAbove = [];
        const expiringSoonCommunalBelow5 = [];
        const expiringSoonResidential = [];

        detectedBuildings.forEach((b) => {
            if (isSkipped(b)) return;

            const floors = b.floors;

            const applyDefaults = (row) => ({
                ...row,
                reinspection: 'Yes',
                visitTwice: row.buildingType.includes('residential') ? 'Yes' : row.visitTwice,
            });

            if (b.buildingType.includes('communal') && floors >= 5 && b.communalLastDate) {
                const nextDue = getNextDueDate(new Date(b.communalLastDate), 3);
                const row = applyDefaults({ ...b, date: formatDate(new Date(b.communalLastDate)), nextDate: formatDate(nextDue), selected: false });

                if (nextDue < today) communal5OrAbove.push(row);
                else if (nextDue >= today && nextDue <= in30Days) expiringSoonCommunal5OrAbove.push(row);
            }

            if (b.buildingType.includes('communal') && floors < 5 && b.communalLastDate) {
                const nextDue = getNextDueDate(new Date(b.communalLastDate), 12);
                const row = applyDefaults({ ...b, date: formatDate(new Date(b.communalLastDate)), nextDate: formatDate(nextDue), selected: false });

                if (nextDue < today) communalBelow5Floors.push(row);
                else if (nextDue >= today && nextDue <= in30Days) expiringSoonCommunalBelow5.push(row);
            }

            if (b.buildingType.includes('residential') && b.residentialLastDate) {
                const nextDue = getNextDueDate(new Date(b.residentialLastDate), 12);
                const row = applyDefaults({ ...b, date: formatDate(new Date(b.residentialLastDate)), nextDate: formatDate(nextDue), selected: false });

                if (nextDue < today) residentialBuildings.push(row);
                else if (nextDue >= today && nextDue <= in30Days) expiringSoonResidential.push(row);
            }
        });

        return {
            overdue: {
                communal5OrAbove,
                communalBelow5Floors,
                residentialBuildings,
            },
            expiringSoon: {
                communal5OrAbove: expiringSoonCommunal5OrAbove,
                communalBelow5Floors: expiringSoonCommunalBelow5,
                residentialBuildings: expiringSoonResidential,
            },
        };
    }

    const smartReSchedule = () => {
        if (!availableFrom || !availableTo) {
            alert('Please select both Available From and Available To dates.');
            return;
        }

        const allOverdueBuildingsRaw = [
            ...overdue.communal5OrAbove,
            ...overdue.communalBelow5Floors,
            ...overdue.residentialBuildings,
        ].filter((r) => r.selected);

        if (!allOverdueBuildingsRaw || allOverdueBuildingsRaw.length === 0) {
            alert('No buildings selected to re-schedule.');
            return;
        }

        const mergedByName = new Map();

        allOverdueBuildingsRaw.forEach((b) => {
            const key = b.buildingName;
            const isResidential = b.buildingType === 'Residential';

            if (!mergedByName.has(key)) {
                mergedByName.set(key, { ...b });
            } else {
                const existing = mergedByName.get(key);

                if (isResidential) {
                    mergedByName.set(key, {
                        ...b,
                        totalCommunalDoors: existing.totalCommunalDoors || b.totalCommunalDoors,
                    });
                } else {
                    mergedByName.set(key, {
                        ...existing,
                        totalCommunalDoors: b.totalCommunalDoors || existing.totalCommunalDoors,
                    });
                }
            }
        });

        const mergedBuildings = Array.from(mergedByName.values());

        const allOverdueBuildings = mergedBuildings.map(({ uid, date, selected, ...rest }) => rest);

        scheduleInspections(availableFrom, availableTo, allOverdueBuildings, unavailableSaveRanges);
    };

    // ============== Smart Scheduling ================

    const userId = 'rxaqnUdohkQBw3ZiH77vl2Eu4rT2';
    const [isScheduling, setIsScheduling] = useState(false);
    const [dots, setDots] = useState('');
    const [inspectors, setInspectors] = useState([]);
    const [selectedInspectors, setSelectedInspectors] = useState([]);
    const [selectAll, setSelectAll] = useState(false);

    const toggle = (id) => {
        const updated = selectedInspectors.includes(id)
            ? selectedInspectors.filter((i) => i !== id)
            : [...selectedInspectors, id];
        setSelectedInspectors(updated);
        setSelectAll(updated.length === inspectors.length);
    };

    const handleSelectAll = () => {
        const isAll = selectedInspectors.length === inspectors.length;
        const updated = isAll ? [] : inspectors.map((i) => i.id);
        setSelectedInspectors(updated);
        setSelectAll(!isAll);
    };

    const pct = inspectors.length ? Math.round((selectedInspectors.length / inspectors.length) * 100) : 0;

    const toLocalDateTime = (d) =>
        d.getFullYear() +
        '-' +
        String(d.getMonth() + 1).padStart(2, '0') +
        '-' +
        String(d.getDate()).padStart(2, '0') +
        'T' +
        String(d.getHours()).padStart(2, '0') +
        ':' +
        String(d.getMinutes()).padStart(2, '0') +
        ':' +
        String(d.getSeconds()).padStart(2, '0');

    async function scheduleInspections(startDate, endDate, buildings, unavailableRanges) {
        let interval;
        let slots = [];
        let savedCount = 0;

        try {
            setIsScheduling(true);
            let dotCount = 0;

            interval = setInterval(() => {
                dotCount = (dotCount + 1) % 4;
                setDots('.'.repeat(dotCount));
            }, 500);

            const existingEvents = await fetchExistingEventsFromFirebase(userId, startDate, endDate);

            const existingLocations = existingEvents
                .filter((e) => e.address && e.city)
                .map((e) => ({ address: e.address, city: e.city }))
                .filter((loc, i, arr) => arr.findIndex((l) => l.address === loc.address && l.city === loc.city) === i);

            const travelTimes = await calculateAllTravelTimes(buildings, inspectors, existingLocations);

            slots = await callSchedulerAPI({
                startDate: toLocalDateTime(startDate),
                endDate: toLocalDateTime(endDate),
                buildings,
                inspectors: inspectors.filter((i) => selectedInspectors.includes(i.id)),
                existingEvents,
                unavailableRanges,
                travelTimes,
            });

            savedCount = await saveToFirebase(userId, slots);

            if (savedCount > 0) {
                const earliestSlotMap = getEarliestSlotDates(slots);

                const reportCount = await saveBuildingReports(buildings, earliestSlotMap);

                toast.success(`${reportCount} building report${reportCount > 1 ? 's' : ''} created successfully!`, {
                    autoClose: 3000,
                    pauseOnHover: true,
                    closeOnClick: true,
                    draggable: false,
                    position: 'top-right',
                    style: {
                        backgroundColor: '#e6ffed',
                        color: '#2e7d32',
                        borderLeft: '4px solid #2e7d32',
                    },
                    icon: <FontAwesomeIcon icon={faCircleCheck} size="lg" style={{ color: 'green' }} />,
                });
            }

            return {
                success: true,
                slotsCreated: slots.length,
                slotsSaved: savedCount,
                slots,
            };
        } catch (error) {
            console.error('Scheduling error:', error);
            toast.error(`Scheduling failed: ${error.message}`, {
                toastId: 'scheduling_error',
                autoClose: 5000,
                style: {
                    backgroundColor: '#fee',
                    color: '#c00',
                    borderLeft: '4px solid red',
                },
                icon: <FontAwesomeIcon icon={faCircleXmark} size="lg" style={{ color: 'red' }} />,
            });
            return {
                success: false,
                error: error.message,
            };
        } finally {
            if (interval) clearInterval(interval);
            setDots('');
            setIsScheduling(false);

            setBuildings([]);
            setAvailableFrom(null);
            setAvailableTo(null);
            setUnavailableRanges([]);
            setUnavailableSaveRanges([]);
            setFrom(null);
            setTo(null);

            if (toRef.current && toRef.current.flatpickr) {
                toRef.current.flatpickr.clear();
            }

            if (slots.length) {
                toast.success(`Scheduling completed! ${slots.length} events created, ${savedCount} saved.`, {
                    toastId: 'scheduling_success',
                    autoClose: 3000,

                    style: {
                        backgroundColor: '#e6ffed',
                        color: '#2e7d32',
                        borderLeft: '4px solid #2e7d32',
                    },
                    icon: <FontAwesomeIcon icon={faCircleCheck} size="lg" style={{ color: 'green' }} />,
                });
                onDirectClose?.();
                onScheduled?.();

                setTimeout(() => {
                    window.location.reload();
                }, 3000);
            } else if (!savedCount) {
                toast.info('No events were scheduled.', {
                    toastId: 'scheduling_info',
                    autoClose: 3000,
                    style: {
                        backgroundColor: '#fff8e1',
                        color: '#8d6e63',
                        borderLeft: '4px solid #ffa000',
                    },
                    icon: <FontAwesomeIcon icon={faCircleXmark} size="lg" style={{ color: 'yellow' }} />,
                });
            }
        }
    }

    useEffect(() => {
        async function loadInspectors() {
            const fetched = await fetchInspectorsFromFirebase();
            setInspectors(fetched);
            setSelectedInspectors(fetched.map((i) => i.id));
            setSelectAll(true);
        }
        loadInspectors();
    }, []);

    async function fetchInspectorsFromFirebase() {
        try {
            const q = query(collection(db, 'users'), where('user_role', '==', 'inspector'));
            const snap = await getDocs(q);
            return snap.docs.map((d) => ({
                id: d.id,
                name: d.data().name || 'Unknown',
            }));
        } catch (error) {
            console.error('Error fetching inspectors:', error);
            return [];
        }
    }

    async function fetchExistingEventsFromFirebase(userId, startDate, endDate) {
        try {
            const calendarRef = collection(db, 'users', userId, 'calendar');
            const snapshot = await getDocs(calendarRef);

            const events = [];
            snapshot.docs.forEach((doc) => {
                const event = doc.data();
                if (!event.start || !event.end || !event.assignedInspector) return;

                const startParts = event.start.split(' ');
                const [sd, sm, sy] = startParts[0].split('/').map(Number);
                const eventDate = new Date(sy, sm - 1, sd);

                if (eventDate >= startDate && eventDate <= endDate) {
                    events.push({
                        start: event.start,
                        end: event.end,
                        assignedInspector: event.assignedInspector,
                        title: event.title,
                        address: event.location ? event.location.substring(0, event.location.lastIndexOf(',')).trim() : null,
                        city: event.location ? event.location.substring(event.location.lastIndexOf(',') + 1).trim() : null,
                    });
                }
            });

            return events;
        } catch (error) {
            console.error('Error fetching existing events:', error);
            return [];
        }
    }

    async function calculateAllTravelTimes(buildings, inspectors, existingLocations = []) {
        const OFFICE_ADDRESS = 'Foundation House';
        const OFFICE_CITY = 'Reigate';

        const travelTimes = {};
        const locations = new Set([`${OFFICE_ADDRESS}|${OFFICE_CITY}`]);

        buildings.forEach((b) => {
            locations.add(`${b.buildingAddress}|${b.buildingCity || ''}`);
        });

        existingLocations.forEach((loc) => {
            locations.add(`${loc.address}|${loc.city || ''}`);
        });

        const locationArray = Array.from(locations);
        let calculated = 0;
        let fromCache = 0;
        let fromAPI = 0;

        for (let i = 0; i < locationArray.length; i++) {
            for (let j = i + 1; j < locationArray.length; j++) {
                const [from, fromCity] = locationArray[i].split('|');
                const [to, toCity] = locationArray[j].split('|');

                const norm = (s) => (s || '').trim().toLowerCase();
                const key = `${norm(from)}|${norm(fromCity)}|${norm(to)}|${norm(toCity)}`;
                const reverseKey = `${norm(to)}|${norm(toCity)}|${norm(from)}|${norm(fromCity)}`;

                if (from === to && fromCity === toCity) {
                    travelTimes[key] = { minutes: 0, miles: 0 };
                    travelTimes[reverseKey] = { minutes: 0, miles: 0 };
                    continue;
                }

                const result = await getTravelTimeWithCache(from, fromCity, to, toCity);

                travelTimes[key] = { minutes: result.minutes, miles: result.miles };
                travelTimes[reverseKey] = { minutes: result.minutes, miles: result.miles };

                if (result.fromCache) fromCache++;
                else fromAPI++;

                calculated++;
            }
        }

        return travelTimes;
    }

    async function getTravelTimeWithCache(from, fromCity, to, toCity) {
        if (from === to && fromCity === toCity) {
            return { minutes: 0, miles: 0, fromCache: true };
        }

        const norm = (s) => (s || '').trim().toLowerCase();
        const cacheKey = `${norm(from)}|${norm(fromCity)}|${norm(to)}|${norm(toCity)}`;
        const reverseKey = `${norm(to)}|${norm(toCity)}|${norm(from)}|${norm(fromCity)}`;

        const stored = localStorage.getItem('travel_' + cacheKey) || localStorage.getItem('travel_' + reverseKey);
        if (stored) {
            try {
                const parsed = JSON.parse(stored);
                return { ...parsed, fromCache: true };
            } catch { }
        }

        try {
            const response = await fetch('/api/getDistance', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    baseAddress: from,
                    baseCity: fromCity,
                    destAddress: to,
                    destCity: toCity,
                }),
            });

            if (!response.ok) throw new Error('Distance API error');

            const data = await response.json();
            const result = {
                minutes: Math.ceil((data.durationMinutes || 30) * 1.2 + 10),
                miles: data.distanceMiles || 10,
            };

            localStorage.setItem('travel_' + cacheKey, JSON.stringify(result));
            localStorage.setItem('travel_' + reverseKey, JSON.stringify(result));

            return { ...result, fromCache: false };
        } catch (error) {
            console.warn(`Failed to get distance: ${from} → ${to}, using fallback`, error);
            return { minutes: 30, miles: 10, fromCache: false };
        }
    }

    async function callSchedulerAPI(payload) {
        try {
            const response = await fetch('/api/scheduleInspections', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.error || 'Scheduler API error');
            }

            const result = await response.json();

            if (!result.success) {
                throw new Error(result.error || 'Scheduling failed');
            }

            return result.slots;
        } catch (error) {
            throw error;
        }
    }

    async function saveToFirebase(userId, slots) {
        let savedCount = 0;

        for (const slot of slots) {
            try {
                await addDoc(collection(db, 'users', userId, 'calendar'), {
                    eventId: slot.eventId,
                    title: ``,
                    start: slot.start,
                    end: slot.end,
                    buildingId: slot.buildingId,
                    buildingAddress: slot.buildingAddress,
                    buildingCity: slot.buildingCity,
                    buildingEmail: slot.buildingEmail,
                    postcode: slot.postcode,
                    location: `${slot.buildingAddress}, ${slot.buildingCity}`,
                    totalResiDoors: slot.totalResiDoors,
                    totalCommunalDoors: slot.totalCommunalDoors,
                    resiDoorsCompleted: slot.resiDoorsCompleted,
                    communalDoorsCompleted: slot.communalDoorsCompleted,
                    isFirstVisit: slot.isFirstVisit,
                    isCallback: slot.isCallback,
                    visitType: slot.visitType,
                    travelTimeMinutes: slot.travelTimeMinutes,
                    travelMiles: slot.travelMiles,
                    surveyTimeMinutes: slot.surveyTimeMinutes,
                    includesLunchBreak: slot.includesLunchBreak,
                    lunchDuration: slot.lunchDuration,
                    isOvertime: slot.isOvertime,
                    overtimeMinutes: slot.overtimeMinutes,
                    assignedInspector: slot.inspectorId,
                    inspectorName: slot.inspectorName,
                    isTeamWork: slot.isTeamWork,
                    teamSize: slot.teamSize,
                    buildingCompleted: slot.buildingCompleted,
                    firstVisitCompleted: slot.firstVisitCompleted,
                    callbackCompleted: slot.callbackCompleted,
                    isSmallBuilding: slot.isSmallBuilding,
                    visitTwice: slot.visitTwice,
                    isReinspection: slot.isReinspection,
                    createdAt: new Date().toISOString(),
                });
                savedCount++;
            } catch (error) {
                console.error('Error saving slot:', error);
            }
        }

        return savedCount;
    }

    const smartSchedule = () => {
        if (!availableFrom || !availableTo) {
            alert('Please select both Available From and Available To dates.');
            return;
        }

        if (!buildings || buildings.length === 0) {
            alert('No buildings loaded to schedule.');
            return;
        }

        scheduleInspections(availableFrom, availableTo, buildings, unavailableSaveRanges);
    };

    const saveBuildingReports = async (buildingsList, earliestSlotMap) => {
        if (!buildingsList.length) return 0;

        let successCount = 0;

        for (const building of buildingsList) {
            const key = `${building.buildingAddress}|${building.buildingCity}`;
            const plannedDateVal = earliestSlotMap[key]?.date || '';
            const assignedInspector = earliestSlotMap[key]?.assignedInspector || '';
            const inspectorName = earliestSlotMap[key]?.inspectorName || '';

            const now = new Date();
            const formattedDate = format(now, 'yyyy-MM-dd HH:mm:ss');

            const data = {
                reportId: '',
                completedBy: inspectorName || '',
                completedByUid: assignedInspector || '',

                buildingName: building.buildingName || '',
                clientAddress: building.clientAddress || '',
                city: building.buildingCity || '',
                postcode: building.postcode || '',
                floors: building.floors || '',
                buildingType: building.buildingType || '',
                clientName: building.clientName || '',
                email: building.buildingEmail || '',
                personInCharge: building.accountablePerson || '',
                totalCommunalDoors: building.totalCommunalDoors || 0,
                totalResiDoors: building.totalResiDoors || 0,
                dateCreated: formattedDate,
                date: plannedDateVal,

                client_address: '',
                client_city: '',
                client_postcode: '',
                nextDate: '',
                nextDateResidential: '',
                uid: '',
                surveryCompleted: false,
                buildingLink: '',
                logo: '',
                contractorCertificate: '',
                addedBy: user?.uid || '',
            };

            try {
                const docRef = await addDoc(collection(db, 'Reports'), data);
                await updateDoc(doc(db, 'Reports', docRef.id), {
                    uid: docRef.id,
                });
                successCount++;
            } catch (error) {
                console.error('Error saving report:', error);
            }
        }

        return successCount;
    };

    const getEarliestSlotDates = (slots = []) => {
        const map = {};

        for (const slot of slots) {
            if (!slot.buildingAddress || !slot.buildingCity || !slot.start) continue;

            const key = `${slot.buildingAddress.trim()}|${slot.buildingCity.trim()}`;
            const parsedStart = parse(slot.start, 'dd/MM/yyyy HH:mm:ss', new Date());

            if (!map[key] || parsedStart < map[key].parsedDate) {
                map[key] = {
                    date: format(parsedStart, 'yyyy-MM-dd HH:mm:ss'),
                    assignedInspector: slot.inspectorId || '',
                    inspectorName: slot.inspectorName || '',
                };
            }
        }

        return map;
    };

    function handleExcelUpload(event) {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();

        reader.onload = (evt) => {
            try {
                const binary = evt.target.result;
                const workbook = XLSX.read(binary, { type: 'binary' });

                const sheetName = workbook.SheetNames[0];
                const sheet = workbook.Sheets[sheetName];

                const headerRow =
                    XLSX.utils.sheet_to_json(sheet, {
                        header: 1,
                        defval: '',
                    })[0] || [];

                const missingColumns = REQUIRED_COLUMNS.filter((col) => !headerRow.includes(col));

                if (missingColumns.length > 0) {
                    toast.info(`Invalid Excel format. Missing columns: ${missingColumns.join(', ')}`, {
                        toastId: 'invalid_excel_template',
                        autoClose: 3000,
                    });
                    setBuildings([]);
                    return;
                }

                const rows = XLSX.utils.sheet_to_json(sheet, {
                    defval: '',
                    trim: true,
                });

                const buildings = rows.map((row) => ({
                    buildingName: row['Property Name'] || '',
                    clientAddress: row['Building Address'] || '',

                    buildingAddress: `${(row['Property Name'] || '').trim()}, ${(row['Building Address'] || '').trim()}`,
                    buildingCity: (row['Building City'] || '').trim(),
                    totalCommunalDoors: Number(row['Communal Doors'] || 0),
                    totalResiDoors: Number(row['Residential Doors'] || 0),
                    visitTwice: (row['Visit Twice'] || '').toLowerCase() === 'yes' ? 'Yes' : 'No',
                    reinspection: (row['Re-inspection'] || '').toLowerCase() === 'yes' ? 'Yes' : 'No',
                    buildingEmail: row['Client Email'] || '',

                    postcode: row['Postcode'] || '',
                    floors: row['Floors'] || '',
                    buildingType: row['Building Type'] || '',
                    clientName: row['Client Name'] || '',
                    accountablePerson: row['Accountable Person'] || '',
                }));

                setBuildings(buildings);
            } catch (err) {
                console.error('Excel parsing failed:', err);
                toast.error('Failed to read Excel file. Please try again.');
                setBuildings([]);
            }
        };

        reader.readAsBinaryString(file);
    }

    const handleFromChange = (dates) => {
        const selectedFrom = dates[0];
        if (!selectedFrom) return;

        const fromWithTime = new Date(selectedFrom);
        fromWithTime.setHours(9, 0, 0, 0);
        setFrom(fromWithTime);

        const toWithTime = new Date(selectedFrom);
        toWithTime.setHours(17, 0, 0, 0);

        if (toRef.current && toRef.current.flatpickr) {
            const dayStart = new Date(selectedFrom);
            dayStart.setHours(0, 0, 0, 0);

            const dayEnd = new Date(selectedFrom);
            dayEnd.setHours(23, 59, 59, 999);

            toRef.current.flatpickr.set('minDate', dayStart);
            toRef.current.flatpickr.set('maxDate', dayEnd);
            toRef.current.flatpickr.setDate(toWithTime, true);
        }

        setTo(toWithTime);
    };

    const handleToChange = (dates) => {
        if (dates && dates[0]) {
            setTo(dates[0]);
        }
    };

    const addRange = () => {
        if (!from || !to) return alert('Please select both From and To.');
        if (from >= to) return alert('From must be before To.');

        setUnavailableRanges((prev) => [...prev, { from, to }]);

        const formatDateForSaving = (date) => {
            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            const hours = String(d.getHours()).padStart(2, '0');
            const minutes = String(d.getMinutes()).padStart(2, '0');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
        };

        setUnavailableSaveRanges((prev) => [
            ...prev,
            {
                from: formatDateForSaving(from),
                to: formatDateForSaving(to),
            },
        ]);

        setFrom(null);
        setTo(null);
    };

    const removeRange = (index) => {
        setUnavailableRanges((prev) => prev.filter((_, i) => i !== index));
        setUnavailableSaveRanges((prev) => prev.filter((_, i) => i !== index));
    };

    const formatDateForScheduling = (date) => {
        if (!date) return '';
        const d = new Date(date);

        let hours = d.getHours();
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';

        hours = hours % 12;
        if (hours === 0) hours = 12;
        const hoursStr = String(hours).padStart(2, '0');

        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();

        return `${day}/${month}/${year} ${hoursStr}:${minutes} ${ampm}`;
    };

    const uploadFile = async (file, docId, fieldName) => {
        const storageRef = ref(storage, `Reports/${docId}/${fieldName}`);
        await uploadBytes(storageRef, file);
        const downloadURL = await getDownloadURL(storageRef);
        await updateDoc(doc(db, 'Reports', docId), {
            [fieldName]: downloadURL,
        });
    };

    const handleSubmitDetails = async () => {
        if (!formRef.current) {
            console.error('Form reference not found');
            return;
        }

        const formData = new FormData(formRef.current);

        const fieldLabels = {
            propertySurveyed: 'Property Surveyed',
            reportId: 'Report ID',
            buildingType: 'Building Type',

            buildingAddress: 'Building Address',
            buildingCity: 'Building City',
            buildingPostcode: 'Building Postcode',
            floors: 'Number of Floors',

            clientName: 'Client Name',
            email: 'Client Email',
            accountablePerson: 'Accountable Person',

            date: 'Planned visit date',
        };

        const requiredFields = Object.keys(fieldLabels);

        const missingFields = requiredFields.filter((field) => {
            const value = formData.get(field);
            return !value || value.trim() === '';
        });

        if (missingFields.length > 0) {
            const missingLabels = missingFields.map((field) => fieldLabels[field]);
            alert(`Please fill all required fields:\n\n${missingLabels.join('\n')}`);
            return;
        }

        setSubmitting(true);

        const now = new Date();
        const formattedDate = format(now, 'yyyy-MM-dd HH:mm:ss');

        const buildingPhotoInput = formRef.current.querySelector('#buildingPhoto');
        const logoInput = formRef.current.querySelector('#buildingLogo');
        const contractorCertificateInput = formRef.current.querySelector('#contractorCertificate');

        const buildingPhoto = buildingPhotoInput?.files?.[0] || null;
        const logo = logoInput?.files?.[0] || null;
        const contractorCertificate = contractorCertificateInput?.files?.[0] || null;

        const totalCommunal = parseInt(formData.get('totalCommunalDoors')) || 0;
        const totalResi = parseInt(formData.get('totalResiDoors')) || 0;

        const dateValue = formData.get('date') || '';
        const dateTime = dateValue ? `${dateValue} 10:00:00` : '';

        const data = {
            reportId: formData.get('reportId') || '',
            clientName: formData.get('clientName') || '',
            clientAddress: formData.get('buildingAddress') || '',
            client_address: '',
            client_city: '',
            client_postcode: '',
            city: formData.get('buildingCity') || '',
            postcode: formData.get('buildingPostcode') || '',
            buildingName: formData.get('propertySurveyed') || '',
            floors: formData.get('floors') || '',
            buildingType: formData.get('buildingType') || '',
            personInCharge: formData.get('accountablePerson') || '',
            nextDate: formData.get('nextCommunalSurveyDate') || '',
            nextDateResidential: formData.get('nextResidentialSurveyDate') || '',
            dateCreated: formattedDate,
            date: dateTime,
            uid: '',
            surveryCompleted: false,
            buildingLink: '',
            logo: '',
            email: formData.get('email') || '',
            addedBy: user?.uid || '',
            totalCommunalDoors: totalCommunal,
            totalResiDoors: totalResi,
            contractorCertificate: '',
            completedBy: selectedBuildingInspector?.label || '',
            completedByUid: selectedBuildingInspector?.value || '',
        };

        try {
            const docRef = await addDoc(collection(db, 'Reports'), data);
            await updateDoc(doc(db, 'Reports', docRef.id), {
                uid: docRef.id,
            });

            if (buildingPhoto) {
                await uploadFile(buildingPhoto, docRef.id, 'buildingLink');
            }
            if (logo) {
                await uploadFile(logo, docRef.id, 'logo');
            }
            if (contractorCertificate) {
                await uploadFile(contractorCertificate, docRef.id, 'contractorCertificate');
            }

            toast.success(`Report for ${data.buildingName} created successfully!`, {
                toastId: `report_success_${docRef.id}`,
                icon: <FontAwesomeIcon icon={faCircleCheck} size="lg" style={{ color: 'green' }} />,
                autoClose: 3000,
                style: {
                    backgroundColor: '#e6ffed',
                    color: '#064e3b',
                    borderLeft: '4px solid green',
                },
            });
        } catch (error) {
            console.error('Error saving report:', error);
        } finally {
            onDirectClose?.();
            setSubmitting(false);
            onSaved?.();
        }
    };

    useEffect(() => {
        if (directOpen) {
            setActiveTab(firstTab);
            setDistance(null);
            setDuration(null);
            setTotalTime(null);
            setSelectedBuildingInspector(null);
            setSelectedBuildingType(null);
            setPlannedDate(today);
            setCommDate(null);
            setResiDate(null);
            setBuildingName('');
            resetTicketFields();
            formRef.current?.reset();
        }
    }, [directOpen]);

    return (
        <BootstrapModal
            className="details-modal"
            show={directOpen}
            onHide={() => onDirectClose?.()}
            centered={!isMobile}
            backdrop="static"
            keyboard={false}
            size="xl"
            scrollable
        >
            <BootstrapModal.Body>
                <Form ref={formRef}>
                    <Tabs
                        defaultActiveKey={firstTab}
                        id="building-details-tabs"
                        activeKey={activeTab}
                        onSelect={handleSelect}
                        className="mb-3 border-bottom"
                        style={{
                            display: 'flex',
                            overflowX: 'auto',
                            overflowY: 'hidden',
                            whiteSpace: 'nowrap',
                            width: '100%',
                            flexWrap: 'nowrap',
                            paddingLeft: '1rem',
                            gap: '1rem',
                            scrollPaddingLeft: '1rem',
                        }}
                    >
                        {tabs.includes('section1') && (
                            <Tab
                                style={{ minHeight: '630px', maxHeight: '630px' }}
                                eventKey="section1"
                                title={
                                    <span
                                        className="text-danger"
                                        style={{
                                            fontSize: window.innerWidth < 768 ? '0.8rem' : '1.1rem',
                                        }}
                                    >
                                        {TAB_TITLES.section1}
                                    </span>
                                }
                                disabled={isScheduling}
                            >
                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">PROPERTY & REPORT</small>
                                </div>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group controlId="propertySurveyed" className="mb-3">
                                            <Form.Label>Property Surveyed *</Form.Label>
                                            <Form.Control
                                                name="propertySurveyed"
                                                type="text"
                                                placeholder="e.g. Building Name"
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    const reportInput = document.querySelector('input[name="reportId"]');
                                                    if (reportInput) {
                                                        reportInput.value = val ? `${val} Fire Door Report 1` : '';
                                                    }
                                                    setBuildingName(val);
                                                    if (!tbTicketTitleTouched) setTbTicketTitle(val);
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="reportId" className="mb-3">
                                            <Form.Label>Report ID *</Form.Label>
                                            <Form.Control name="reportId" type="text" placeholder="Enter Report ID" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="completedBy" className="mb-3">
                                            <Form.Label>
                                                Report Inspected By
                                            </Form.Label>
                                            <CreatableSelect
                                                options={inspectores}
                                                value={selectedBuildingInspector}
                                                onChange={setSelectedBuildingInspector}
                                                placeholder="Select inspector"
                                                isClearable
                                                isSearchable
                                                menuPlacement="auto"
                                                menuPosition="fixed"
                                                menuPortalTarget={document.body}
                                                formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                                                styles={selectStyles}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="buildingType" className="mb-3">
                                            <Form.Label>Building Type *</Form.Label>
                                            <Select
                                                name="buildingType"
                                                options={buildingTypeOptions}
                                                value={selectedBuildingType}
                                                onChange={setSelectedBuildingType}
                                                placeholder="Select Building Type"
                                                isClearable
                                                isSearchable
                                                menuPlacement="auto"
                                                menuPosition="fixed"
                                                menuPortalTarget={document.body}
                                                styles={selectStyles}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">LOCATION & STRUCTURE</small>
                                </div>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group controlId="buildingAddress" className="mb-3">
                                            <Form.Label>Building Address *</Form.Label>
                                            <Form.Control name="buildingAddress" type="text" placeholder="Building Address" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="buildingCity" className="mb-3">
                                            <Form.Label>Building City *</Form.Label>
                                            <Form.Control name="buildingCity" type="text" placeholder="City" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="buildingPostcode" className="mb-3">
                                            <Form.Label>Building Postcode *</Form.Label>
                                            <Form.Control name="buildingPostcode" type="text" placeholder="Postcode" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="floors" className="mb-3">
                                            <Form.Label>Number of Floors *</Form.Label>
                                            <Form.Control
                                                name="floors"
                                                type="number"
                                                placeholder="e.g. 3"
                                                min={1}
                                                onInput={(e) => {
                                                    if (e.target.value < 1) e.target.value = 1;
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">CLIENT INFORMATION</small>
                                </div>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group controlId="clientName" className="mb-3">
                                            <Form.Label>Client Name *</Form.Label>
                                            <Form.Control name="clientName" type="text" placeholder="Client Name" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="email" className="mb-3">
                                            <Form.Label>Client Email *</Form.Label>
                                            <Form.Control name="email" type="email" placeholder="Client Email" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="accountablePerson" className="mb-3">
                                            <Form.Label>Accountable Person *</Form.Label>
                                            <Form.Control name="accountablePerson" type="text" placeholder="Name of Responsible Person" />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">DOORS & ATTACHMENTS</small>
                                </div>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group controlId="totalCommunalDoors" className="mb-3">
                                            <Form.Label>Number of Communal Doors</Form.Label>
                                            <Form.Control
                                                name="totalCommunalDoors"
                                                type="text"
                                                placeholder="Number of Communal Doors"
                                                onChange={handleCalculateTime}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="totalResiDoors" className="mb-3">
                                            <Form.Label>Number of Residential Doors</Form.Label>
                                            <Form.Control
                                                name="totalResiDoors"
                                                type="text"
                                                placeholder="Number of Residential Doors"
                                                onChange={handleCalculateTime}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="buildingPhoto" className="mb-3">
                                            <Form.Label>Photo of Building</Form.Label>
                                            <Form.Control name="buildingPhoto" type="file" accept="image/*" />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="buildingLogo" className="mb-3">
                                            <Form.Label>Logo of Building</Form.Label>
                                            <Form.Control name="buildingLogo" type="file" accept="image/*" />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">UPCOMING SCHEDULE</small>
                                </div>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group controlId="date" className="mb-3">
                                            <Form.Label>Planned visit date *</Form.Label>
                                            <Flatpickr
                                                name="date"
                                                className="form-control"
                                                value={plannedDate}
                                                onChange={([date]) => setPlannedDate(date)}
                                                options={{
                                                    altInput: true,
                                                    altFormat: 'd/m/Y',
                                                    dateFormat: 'Y-m-d',
                                                    monthSelectorType: 'static',
                                                    disableMobile: true,
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="nextCommunalSurveyDate" className="mb-3">
                                            <Form.Label>Next Communal Date</Form.Label>
                                            <Flatpickr
                                                name="nextCommunalSurveyDate"
                                                className="form-control"
                                                value={commDate}
                                                onChange={([date]) => setCommDate(date)}
                                                options={{
                                                    altInput: true,
                                                    altFormat: 'd/m/Y',
                                                    dateFormat: 'Y-m-d',
                                                    monthSelectorType: 'static',
                                                    disableMobile: true,
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>

                                    <Col md={3}>
                                        <Form.Group controlId="nextResidentialSurveyDate" className="mb-3">
                                            <Form.Label>Next Residential Date</Form.Label>
                                            <Flatpickr
                                                name="nextResidentialSurveyDate"
                                                className="form-control"
                                                value={resiDate}
                                                onChange={([date]) => setResiDate(date)}
                                                options={{
                                                    altInput: true,
                                                    altFormat: 'd/m/Y',
                                                    dateFormat: 'Y-m-d',
                                                    monthSelectorType: 'static',
                                                    disableMobile: true,
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Tab>
                        )}

                        {tabs.includes('section2') && (
                            <Tab
                                style={{ minHeight: '630px', maxHeight: '630px' }}
                                eventKey="section2"
                                title={
                                    <span
                                        className="text-danger"
                                        style={{
                                            fontSize: window.innerWidth < 768 ? '0.8rem' : '1.1rem',
                                        }}
                                    >
                                        {TAB_TITLES.section2}
                                    </span>
                                }
                                disabled={isScheduling}
                            >
                                {isScheduling && (
                                    <div
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            margin: '1rem 0',
                                            padding: '0.75rem 1rem',
                                            backgroundColor: '#f0f8ff',
                                            borderRadius: '8px',
                                            border: '1px solid #b0c4de',
                                        }}
                                    >
                                        <div style={{ textAlign: 'center' }}>
                                            <small
                                                style={{
                                                    color: '#424141ff',
                                                    fontWeight: 'bold',
                                                    fontSize: '0.9rem',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                <i className="bi bi-file-earmark-spreadsheet me-2" style={{ fontSize: '1.1rem' }}></i>
                                                Scheduling buildings{dots}
                                            </small>
                                        </div>
                                    </div>
                                )}

                                <Row className="g-3" style={{ minHeight: '580px' }}>
                                    <Col md={7} className="d-flex flex-column gap-3">
                                        <div>
                                            <div className="mb-2 py-2 bg-primary bg-opacity-10 border-start border-primary border-4 rounded-end">
                                                <small className="text-primary fw-bold">
                                                    <i className="bi bi-file-earmark-spreadsheet ms-2 me-2"></i>
                                                    LOAD BUILDINGS DATA
                                                </small>
                                            </div>
                                            <Form.Group className="mt-2">
                                                <Form.Label className="fw-semibold mb-2">
                                                    <i className="bi bi-upload me-1"></i>
                                                    Upload Excel File
                                                </Form.Label>
                                                <Form.Control
                                                    type="file"
                                                    accept=".xlsx,.xls"
                                                    onChange={handleExcelUpload}
                                                    style={{ height: '38px', maxWidth: '300px' }}
                                                    disabled={isScheduling}
                                                />
                                            </Form.Group>
                                        </div>

                                        <div>
                                            <div className="mb-2 py-2 bg-success bg-opacity-10 border-start border-success border-4 rounded-end">
                                                <small className="text-success fw-bold">
                                                    <i className="bi bi-calendar-check ms-2 me-2"></i>
                                                    AVAILABILITY PERIOD
                                                </small>
                                            </div>
                                            <Row className="g-3">
                                                <Col xs={12} sm={6}>
                                                    <Form.Group className="d-flex flex-column">
                                                        <Form.Label className="fw-semibold mb-2 d-flex align-items-center">
                                                            <i className="bi bi-clock me-1"></i>
                                                            Available From
                                                        </Form.Label>
                                                        <Flatpickr
                                                            value={availableFrom}
                                                            onChange={(dates) => {
                                                                if (!dates[0]) return;
                                                                setAvailableFrom(dates[0]);
                                                                if (!availableTo) setAvailableTo(dates[0]);
                                                            }}
                                                            options={{
                                                                enableTime: false,
                                                                altInput: true,
                                                                altFormat: 'd/m/Y',
                                                                dateFormat: 'Y-m-d',
                                                                allowInput: true,
                                                                disableMobile: true,
                                                                static: true,
                                                                monthSelectorType: 'static',
                                                            }}
                                                            className="form-control"
                                                            style={{ minHeight: '38px' }}
                                                            disabled={isScheduling}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                                <Col xs={12} sm={6}>
                                                    <Form.Group className="d-flex flex-column">
                                                        <Form.Label className="fw-semibold mb-2 d-flex align-items-center">
                                                            <i className="bi bi-clock-fill me-1"></i>
                                                            Available To
                                                        </Form.Label>
                                                        <Flatpickr
                                                            value={availableTo}
                                                            onChange={(dates) => setAvailableTo(dates[0])}
                                                            options={{
                                                                enableTime: false,
                                                                altInput: true,
                                                                altFormat: 'd/m/Y',
                                                                dateFormat: 'Y-m-d',
                                                                minDate: availableFrom || new Date(),
                                                                allowInput: true,
                                                                disableMobile: true,
                                                                static: true,
                                                                monthSelectorType: 'static',
                                                            }}
                                                            className="form-control"
                                                            style={{ minHeight: '38px' }}
                                                            disabled={isScheduling}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </div>

                                        <div>
                                            <div className="mb-2 py-2 bg-warning bg-opacity-10 border-start border-warning border-4 rounded-end">
                                                <small className="text fw-bold">
                                                    <i className="bi bi-people ms-2 me-2"></i>
                                                    SELECT INSPECTORS
                                                </small>
                                            </div>

                                            <div
                                                onClick={handleSelectAll}
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 10,
                                                    padding: '9px 14px',
                                                    borderRadius: 8,
                                                    border: '0.5px solid #d0d5dd',
                                                    background: '#f9fafb',
                                                    cursor: 'pointer',
                                                    marginBottom: 12,
                                                    userSelect: 'none',
                                                }}
                                                onMouseEnter={(e) => (e.currentTarget.style.background = '#f0f4f8')}
                                                onMouseLeave={(e) => (e.currentTarget.style.background = '#f9fafb')}
                                            >
                                                <CustomCheck checked={selectedInspectors.length === inspectors.length && inspectors.length > 0} />
                                                <span style={{ fontSize: 13, fontWeight: 500, flex: 1, color: '#1a1a1a' }}>Select all inspectors</span>
                                                <span style={{ fontSize: 11, color: '#aaa' }}>
                                                    {selectedInspectors.length}/{inspectors.length}
                                                </span>
                                            </div>

                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))', gap: 8 }}>
                                                {inspectors.map((inspector, idx) => {
                                                    const isSelected = selectedInspectors.includes(inspector.id);
                                                    const colorSet = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                                                    return (
                                                        <div
                                                            key={inspector.id}
                                                            onClick={() => toggle(inspector.id)}
                                                            style={{
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'center',
                                                                gap: 6,
                                                                padding: '12px 6px',
                                                                borderRadius: 12,
                                                                textAlign: 'center',
                                                                border: isSelected ? '1.5px solid #378ADD' : '0.5px solid #e2e6ea',
                                                                background: isSelected ? '#E6F1FB' : '#fff',
                                                                cursor: 'pointer',
                                                                userSelect: 'none',
                                                                transition: 'all 0.15s ease',
                                                                position: 'relative',
                                                            }}
                                                            onMouseEnter={(e) => {
                                                                if (!isSelected) {
                                                                    e.currentTarget.style.borderColor = '#a8c8f0';
                                                                    e.currentTarget.style.background = '#f5f9ff';
                                                                }
                                                            }}
                                                            onMouseLeave={(e) => {
                                                                if (!isSelected) {
                                                                    e.currentTarget.style.borderColor = '#e2e6ea';
                                                                    e.currentTarget.style.background = '#fff';
                                                                }
                                                            }}
                                                        >
                                                            <div style={{ position: 'absolute', top: 6, right: 6 }}>
                                                                <CustomCheck checked={isSelected} size={14} />
                                                            </div>
                                                            <div
                                                                style={{
                                                                    width: 38,
                                                                    height: 38,
                                                                    borderRadius: '50%',
                                                                    background: colorSet.bg,
                                                                    color: colorSet.text,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    flexShrink: 0,
                                                                    border: isSelected ? '1px solid #0C447C' : '2.5px solid transparent',
                                                                    transition: 'border 0.15s ease',
                                                                }}
                                                            >
                                                                {getInitials(inspector.name)}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    fontSize: 11,
                                                                    fontWeight: 500,
                                                                    lineHeight: 1.3,
                                                                    color: isSelected ? '#0C447C' : '#1a1a1a',
                                                                    wordBreak: 'break-word',
                                                                    width: '100%',
                                                                    textTransform: 'capitalize',
                                                                }}
                                                            >
                                                                {inspector.name}
                                                            </div>
                                                            {inspector.role && (
                                                                <div style={{ fontSize: 10, color: isSelected ? '#185FA5' : '#999', lineHeight: 1.2 }}>
                                                                    {inspector.role}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    </Col>

                                    <Col md="auto" className="d-none d-md-flex justify-content-center">
                                        <div style={{ width: '1px', background: '#e2e6ea', minHeight: '100%' }} />
                                    </Col>

                                    <Col md={4} className="d-flex flex-column">
                                        <div className="mb-2 py-2 bg-danger bg-opacity-10 border-start border-danger border-4 rounded-end">
                                            <small className="text-danger fw-bold">
                                                <i className="bi bi-calendar-x ms-2 me-2"></i>
                                                UNAVAILABILITY RANGES
                                            </small>
                                        </div>

                                        <div className="p-3 bg-light border rounded mb-3">
                                            <Row className="g-2">
                                                <Col xs={12}>
                                                    <Form.Group className="d-flex flex-column">
                                                        <Form.Label className="fw-semibold mb-2 small">
                                                            <i className="bi bi-calendar-minus me-1"></i>
                                                            Unavailable From
                                                            <span className="badge bg-secondary ms-2 fw-normal" style={{ fontSize: '0.65rem' }}>
                                                                9 AM - 5 PM
                                                            </span>
                                                        </Form.Label>
                                                        <Flatpickr
                                                            value={from}
                                                            onChange={handleFromChange}
                                                            options={{
                                                                enableTime: true,
                                                                time_24hr: false,
                                                                altInput: true,
                                                                altFormat: 'd/m/Y h:i K',
                                                                dateFormat: 'Y-m-d H:i',
                                                                defaultHour: 9,
                                                                defaultMinute: 0,
                                                                minTime: '09:00',
                                                                maxTime: '17:00',
                                                                allowInput: true,
                                                                disableMobile: true,
                                                                appendTo: document.body,
                                                                static: true,
                                                                minuteIncrement: 15,
                                                            }}
                                                            className="form-control"
                                                            style={{ height: '38px' }}
                                                            disabled={isScheduling}
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col xs={12}>
                                                    <Form.Group className="d-flex flex-column">
                                                        <Form.Label className="fw-semibold mb-2 small">
                                                            <i className="bi bi-calendar-minus-fill me-1"></i>
                                                            Unavailable To
                                                            <span className="badge bg-secondary ms-2 fw-normal" style={{ fontSize: '0.65rem' }}>
                                                                Same Day
                                                            </span>
                                                        </Form.Label>
                                                        <Flatpickr
                                                            ref={toRef}
                                                            value={to}
                                                            onChange={handleToChange}
                                                            options={{
                                                                enableTime: true,
                                                                time_24hr: false,
                                                                altInput: true,
                                                                altFormat: 'd/m/Y h:i K',
                                                                dateFormat: 'Y-m-d H:i',
                                                                defaultHour: 17,
                                                                defaultMinute: 0,
                                                                minTime: '09:00',
                                                                maxTime: '17:00',
                                                                allowInput: true,
                                                                disableMobile: true,
                                                                appendTo: document.body,
                                                                static: true,
                                                                minuteIncrement: 15,
                                                                disable: [
                                                                    function (date) {
                                                                        if (!from) return true;
                                                                        return date.toDateString() !== from.toDateString();
                                                                    },
                                                                ],
                                                            }}
                                                            className="form-control"
                                                            style={{ height: '38px' }}
                                                            disabled={isScheduling}
                                                        />
                                                    </Form.Group>
                                                </Col>

                                                <Col xs={12}>
                                                    <Button
                                                        onClick={addRange}
                                                        variant="primary"
                                                        className="w-100 d-flex align-items-center justify-content-center"
                                                        style={{ height: '38px' }}
                                                        disabled={isScheduling}
                                                    >
                                                        <i className="bi bi-plus-circle-fill me-2"></i>
                                                        Add Range
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </div>

                                        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px' }}>
                                            {unavailableRanges.length > 0 ? (
                                                <>
                                                    <small className="text-muted fw-semibold d-block mb-2">
                                                        <i className="bi bi-list-ul me-1"></i>
                                                        Added Ranges ({unavailableRanges.length})
                                                    </small>
                                                    <div className="d-flex flex-column gap-2">
                                                        {unavailableRanges.map((range, index) => (
                                                            <Card key={index} className="shadow-sm border-start border-danger border-1">
                                                                <Card.Body className="d-flex justify-content-between align-items-center p-2">
                                                                    <div className="flex-grow-1 me-2" style={{ minWidth: 0 }}>
                                                                        <div className="fw-semibold small mb-1">Range #{index + 1}</div>
                                                                        <div className="text-muted small text-truncate">
                                                                            <i className="bi bi-calendar-event me-1"></i>
                                                                            {formatDateForScheduling(range.from)}
                                                                            <span className="mx-1">→</span>
                                                                            {formatDateForScheduling(range.to)}
                                                                        </div>
                                                                    </div>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        onClick={() => removeRange(index)}
                                                                        className="flex-shrink-0"
                                                                        disabled={isScheduling}
                                                                    >
                                                                        <i className="bi bi-trash me-1"></i>
                                                                        Remove
                                                                    </Button>
                                                                </Card.Body>
                                                            </Card>
                                                        ))}
                                                    </div>
                                                </>
                                            ) : (
                                                <div className="text-center py-5 bg-light border border-dashed rounded">
                                                    <div className="mb-3">
                                                        <i className="bi bi-calendar-plus text-muted" style={{ fontSize: '3rem' }}></i>
                                                    </div>
                                                    <h6 className="text-muted mb-2">No Unavailable Ranges</h6>
                                                    <p className="text-muted small mb-0">Add your first unavailable range using the form above</p>
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </Tab>
                        )}

                        {tabs.includes('section3') && (
                            <Tab
                                eventKey="section3"
                                title={
                                    <span
                                        className="text-danger"
                                        style={{
                                            fontSize: window.innerWidth < 768 ? '0.8rem' : '1.1rem',
                                        }}
                                    >
                                        {TAB_TITLES.section3}
                                    </span>
                                }
                            >
                                {loadingReschedule && !overdue && (
                                    <div className="d-flex align-items-center justify-content-center" style={{ height: '650px' }}>
                                        <div className="text-center">
                                            <div className="spinner-border text-danger mb-2" role="status">
                                                <span className="visually-hidden">Loading…</span>
                                            </div>
                                            <div>Loading buildings…</div>
                                        </div>
                                    </div>
                                )}

                                {!loadingReschedule && overdue && (
                                    <div className="row align-items-center mb-3">
                                        <div className="col mb-3">
                                            <div className="alert alert-info mb-0 py-2">
                                                {includeExpiring ? (
                                                    <>Buildings that have expired, or are due to expire within the next month</>
                                                ) : (
                                                    <>
                                                        Buildings that have expired as of <strong>{new Date().toLocaleDateString()}</strong>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        <div className="col-auto">
                                            <div className="form-check form-switch d-flex align-items-center">
                                                <input
                                                    id="includeExpiringSwitch"
                                                    type="checkbox"
                                                    className="form-check-input me-3 mb-2"
                                                    checked={includeExpiring}
                                                    style={{ cursor: 'pointer' }}
                                                    onChange={({ target: { checked } }) => toggleIncludeExpiring(checked)}
                                                />

                                                <label htmlFor="includeExpiringSwitch" className="form-check-label mb-0">
                                                    Include buildings expiring within <strong>next 30 days</strong>
                                                </label>
                                            </div>
                                        </div>

                                        {[
                                            { key: 'communal5OrAbove', title: 'Communal 5+ Floors' },
                                            { key: 'communalBelow5Floors', title: 'Communal Below 5 Floors' },
                                            { key: 'residentialBuildings', title: 'Residential Buildings' },
                                        ].map(({ key, title }) => {
                                            const rows = overdue[key] || [];
                                            if (rows.length === 0) return null;

                                            const change = (rowIndex, field, value) => {
                                                setOverdue((prev) => ({
                                                    ...prev,
                                                    [key]: prev[key].map((r, i) => (i === rowIndex ? { ...r, [field]: value } : r)),
                                                }));
                                            };

                                            const toggleSelected = (rowIndex) => {
                                                setOverdue((prev) => ({
                                                    ...prev,
                                                    [key]: prev[key].map((r, i) => (i === rowIndex ? { ...r, selected: !r.selected } : r)),
                                                }));
                                            };

                                            return (
                                                <div className="mb-4" key={key}>
                                                    <h6 className="fw-bold">{title}</h6>
                                                    <div className="table-responsive">
                                                        <table className="table table-sm table-bordered mb-0" style={{ minWidth: '1500px' }}>
                                                            <thead>
                                                                <tr>
                                                                    <th style={{ width: '40px' }} className="text-center"></th>
                                                                    <th style={{ width: '180px' }}>Property Name</th>
                                                                    <th style={{ width: '200px' }}>Building Address</th>
                                                                    <th style={{ width: '110px' }}>Postcode</th>
                                                                    <th style={{ width: '140px' }}>Building City</th>
                                                                    <th style={{ width: '140px' }}>Building Type</th>
                                                                    <th style={{ width: '70px' }}>Floors</th>
                                                                    <th style={{ width: '120px' }}>Last Inspected</th>
                                                                    <th style={{ width: '120px' }}>Next Due</th>
                                                                    <th style={{ width: '180px' }}>Client Name</th>
                                                                    <th style={{ width: '200px' }}>Client Email</th>
                                                                    <th style={{ width: '160px' }}>Accountable Person</th>
                                                                    <th style={{ width: '90px' }}>Communal Doors</th>
                                                                    <th style={{ width: '90px' }}>Residential Doors</th>
                                                                    <th style={{ width: '90px' }}>Visit Twice</th>
                                                                    <th style={{ width: '90px' }}>Re-inspection</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {rows.map((r, i) => (
                                                                    <tr key={i} style={r.selected ? { backgroundColor: '#fff3f3' } : undefined}>
                                                                        <td className="text-center" style={{ width: '40px', minWidth: '40px', padding: '12px' }}>
                                                                            <input
                                                                                type="checkbox"
                                                                                style={{
                                                                                    cursor: 'pointer',
                                                                                    accentColor: '#dc3545',
                                                                                    width: '16px',
                                                                                    height: '16px',
                                                                                    margin: '0 auto',
                                                                                    display: 'block',
                                                                                }}
                                                                                checked={!!r.selected}
                                                                                onChange={() => toggleSelected(i)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '170px' }}
                                                                                value={r.buildingName}
                                                                                onChange={(e) => change(i, 'buildingName', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '190px' }}
                                                                                value={r.clientAddress}
                                                                                onChange={(e) => change(i, 'clientAddress', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '100px' }}
                                                                                value={r.postcode}
                                                                                onChange={(e) => change(i, 'postcode', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '130px' }}
                                                                                value={r.buildingCity}
                                                                                onChange={(e) => change(i, 'buildingCity', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '130px' }}
                                                                                value={r.buildingType}
                                                                                onChange={(e) => change(i, 'buildingType', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '60px' }}
                                                                                value={r.floors}
                                                                                onChange={(e) => change(i, 'floors', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '110px', backgroundColor: '#f5f5f5' }}
                                                                                value={r.date}
                                                                                readOnly
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '110px', backgroundColor: '#f5f5f5' }}
                                                                                value={r.nextDate}
                                                                                readOnly
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '170px' }}
                                                                                value={r.clientName}
                                                                                onChange={(e) => change(i, 'clientName', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '190px' }}
                                                                                value={r.buildingEmail}
                                                                                onChange={(e) => change(i, 'buildingEmail', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                style={{ width: '100%', minWidth: '150px' }}
                                                                                value={r.accountablePerson}
                                                                                onChange={(e) => change(i, 'accountablePerson', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="number"
                                                                                style={{ width: '100%', minWidth: '80px' }}
                                                                                value={r.totalCommunalDoors}
                                                                                onChange={(e) => change(i, 'totalCommunalDoors', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td>
                                                                            <input
                                                                                type="number"
                                                                                style={{ width: '100%', minWidth: '80px' }}
                                                                                value={r.totalResiDoors}
                                                                                onChange={(e) => change(i, 'totalResiDoors', e.target.value)}
                                                                            />
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <div className="form-check form-switch d-flex justify-content-center">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="checkbox"
                                                                                    role="switch"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    checked={r.visitTwice === 'Yes'}
                                                                                    onChange={(e) => change(i, 'visitTwice', e.target.checked ? 'Yes' : 'No')}
                                                                                />
                                                                            </div>
                                                                        </td>
                                                                        <td className="text-center">
                                                                            <div className="form-check form-switch d-flex justify-content-center">
                                                                                <input
                                                                                    className="form-check-input"
                                                                                    type="checkbox"
                                                                                    role="switch"
                                                                                    style={{ cursor: 'pointer' }}
                                                                                    checked={r.reinspection === 'Yes'}
                                                                                    onChange={(e) => change(i, 'reinspection', e.target.checked ? 'Yes' : 'No')}
                                                                                />
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {!loadingReschedule && !overdue && <div className="p-3">Click tab to load data</div>}
                            </Tab>
                        )}

                        {tabs.includes('section4') && (
                            <Tab
                                style={{ minHeight: '630px', maxHeight: '630px' }}
                                eventKey="section4"
                                title={
                                    <span
                                        className="text-danger"
                                        style={{
                                            fontSize: window.innerWidth < 768 ? '0.8rem' : '1.1rem',
                                        }}
                                    >
                                        {TAB_TITLES.section4}
                                    </span>
                                }
                            >
                                <div className="mb-2 px-2 py-1 bg-light border-start border-danger border-3">
                                    <small className="text-muted fw-bold">
                                        TICKET DETAILS
                                    </small>
                                </div>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ticket Title *</Form.Label>
                                            <Form.Control
                                                placeholder="Auto-filled from property name"
                                                value={tbTicketTitle}
                                                onChange={(e) => {
                                                    setTbTicketTitle(e.target.value);
                                                    setTbTicketTitleTouched(true);
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>
                                                Building{' '}
                                                <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                                    (auto — created with this ticket)
                                                </span>
                                            </Form.Label>
                                            <Form.Control
                                                value={buildingName || `— fill in Property Surveyed on "${TAB_TITLES.section1}" —`}
                                                disabled
                                                style={{ background: '#f5f5f5', }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Description</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                value={tbTicketDescription}
                                                onChange={(e) => setTbTicketDescription(e.target.value)}
                                                style={{
                                                    maxHeight: '200px',
                                                    resize: 'vertical',
                                                    overflowY: 'auto',
                                                }}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Assign To *</Form.Label>
                                            <Form.Select value={tbAssignee} onChange={(e) => setTbAssignee(e.target.value)}>
                                                <option value="">Select person...</option>
                                                {assignableUsers.map((u) => (
                                                    <option key={u.uid} value={u.uid}>
                                                        {u.name || u.email}{u.uid === user?.uid ? ' (me)' : ''}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Priority</Form.Label>
                                            <Form.Select value={tbPriority} onChange={(e) => setTbPriority(e.target.value)}>
                                                {TICKET_PRIORITY_OPTIONS.map((p) => (
                                                    <option key={p} value={p}>{p}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Task Type</Form.Label>
                                            <Form.Select value={tbTaskType} onChange={(e) => setTbTaskType(e.target.value)}>
                                                {TASK_TYPE_OPTIONS.map((t) => (
                                                    <option key={t.value} value={t.value}>{t.label}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Due Date &amp; Time *</Form.Label>
                                            <Form.Control
                                                type="datetime-local"
                                                value={tbDueDate}
                                                onChange={(e) => setTbDueDate(e.target.value)}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Ticket Attachment</Form.Label>
                                            <Form.Control type="file" accept="image/*" onChange={(e) => setTbTicketFile(e.target.files[0])} />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Tab>
                        )}
                    </Tabs>
                </Form>
            </BootstrapModal.Body>

            <BootstrapModal.Footer>
                {activeTab !== 'section3' && (
                    <Button
                        variant="secondary"
                        disabled={isScheduling || submittingTicketBuilding}
                        onClick={() => {
                            onDirectClose?.();
                            setBuildings([]);

                            setAvailableFrom(null);
                            setAvailableTo(null);
                            setUnavailableRanges([]);
                            setUnavailableSaveRanges([]);
                            setFrom(null);
                            setTo(null);

                            if (toRef.current && toRef.current.flatpickr) {
                                toRef.current.flatpickr.clear();
                            }

                            setOverdue(null);
                            setLoadingReschedule(false);
                            setIncludeExpiring(false);
                            setActiveTab(null);
                        }}
                    >
                        Cancel
                    </Button>
                )}

                {activeTab === 'section1' && !isTicketFlow && (
                    <Button variant="danger" disabled={submitting} onClick={handleSubmitDetails}>
                        Save Details
                    </Button>
                )}

                {activeTab === 'section2' && (
                    <Button
                        variant="danger"
                        disabled={isScheduling}
                        onClick={() => {
                            if (userData.role !== 'admin' && userData.role !== 'manager' && userData.role !== 'inspection') {
                                toast.info("You don't have permission to run this!", {
                                    toastId: 'no_permission',
                                    className: 'red-toast',
                                    icon: <FontAwesomeIcon icon={faCircleXmark} size="lg" style={{ color: 'red' }} />,
                                    progressClassName: 'red-progress-bar',
                                    style: {
                                        backgroundColor: '#fee',
                                        color: '#c00',
                                        borderLeft: '4px solid red',
                                    },
                                });
                                return;
                            }

                            smartSchedule();
                        }}
                    >
                        Smart Schedule
                    </Button>
                )}

                {isTicketFlow && activeTab === 'section1' && (
                    <Button
                        variant="danger"
                        onClick={() => setActiveTab('section4')}
                    >
                        Next
                    </Button>
                )}

                {isTicketFlow && activeTab === 'section4' && (
                    <Button
                        variant="danger"
                        disabled={submittingTicketBuilding}
                        onClick={handleCreateBuildingAndTicket}
                    >
                        {submittingTicketBuilding ? 'Creating…' : 'Create Building & Ticket'}
                    </Button>
                )}

                {activeTab === 'section3' && (
                    <div className="d-flex align-items-center w-100 gap-3 flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                            <Form.Label className="fw-semibold mb-0 text-nowrap">
                                <i className="bi bi-clock me-1"></i>
                                Available From
                            </Form.Label>

                            <Flatpickr
                                value={availableFrom}
                                onChange={(dates) => {
                                    if (!dates[0]) return;
                                    setAvailableFrom(dates[0]);
                                    if (!availableTo) setAvailableTo(dates[0]);
                                }}
                                options={{
                                    enableTime: false,
                                    altInput: true,
                                    altFormat: 'd/m/Y',
                                    dateFormat: 'Y-m-d',
                                    allowInput: true,
                                    disableMobile: true,
                                    monthSelectorType: 'static',

                                    position: 'above',
                                    appendTo: document.body,
                                }}
                                className="form-control"
                                style={{ minHeight: '38px', width: '150px' }}
                                disabled={isScheduling}
                            />
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            <Form.Label className="fw-semibold mb-0 text-nowrap">
                                <i className="bi bi-clock-fill me-1"></i>
                                Available To
                            </Form.Label>

                            <Flatpickr
                                value={availableTo}
                                onChange={(dates) => setAvailableTo(dates[0])}
                                options={{
                                    enableTime: false,
                                    altInput: true,
                                    altFormat: 'd/m/Y',
                                    dateFormat: 'Y-m-d',
                                    minDate: availableFrom || new Date(),
                                    allowInput: true,
                                    disableMobile: true,
                                    monthSelectorType: 'static',

                                    position: 'above',
                                    appendTo: document.body,
                                }}
                                className="form-control"
                                style={{ minHeight: '38px', width: '150px' }}
                                disabled={isScheduling}
                            />
                        </div>

                        <div className="ms-auto d-flex align-items-center gap-2">
                            <Button
                                variant="secondary"
                                disabled={isScheduling}
                                onClick={() => {
                                    onDirectClose?.();
                                    setBuildings([]);

                                    setAvailableFrom(null);
                                    setAvailableTo(null);
                                    setUnavailableRanges([]);
                                    setUnavailableSaveRanges([]);
                                    setFrom(null);
                                    setTo(null);

                                    if (toRef.current && toRef.current.flatpickr) {
                                        toRef.current.flatpickr.clear();
                                    }

                                    setOverdue(null);
                                    setLoadingReschedule(false);
                                    setIncludeExpiring(false);
                                    setActiveTab(null);
                                }}
                            >
                                Cancel
                            </Button>

                            <Button
                                variant="danger"
                                disabled={isScheduling}
                                onClick={() => {
                                    if (userData.role !== 'admin' && userData.role !== 'manager' && userData.role !== 'inspection') {
                                        toast.info("You don't have permission to run this!", {
                                            toastId: 'no_permission',
                                            className: 'red-toast',
                                            icon: (
                                                <FontAwesomeIcon
                                                    icon={faCircleXmark}
                                                    size="lg"
                                                    style={{ color: 'red' }}
                                                />
                                            ),
                                            progressClassName: 'red-progress-bar',
                                            style: {
                                                backgroundColor: '#fee',
                                                color: '#c00',
                                                borderLeft: '4px solid red',
                                            },
                                        });
                                        return;
                                    }

                                    smartReSchedule();
                                }}
                            >
                                Smart Re-schedule
                            </Button>
                        </div>
                    </div>
                )}
            </BootstrapModal.Footer>
        </BootstrapModal>
    );
};

export default InspectionModal;